import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useDdMethodology, METHODOLOGIES } from "../../../hooks";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDReportCard } from "../../../components/Admin/DDReportCard";
import { DDPanel } from "../DashboardDD/DashboardDD";
import { loadDdReportData } from "../../../utils/ddReports/ddReportData";
import "./DDHome.scss";

// El generador y sus diccionarios (ES/EN/PT) pesan: se cargan recién al generar el informe.
async function buildDdReport(lang, accessToken) {
  const [{ buildDdReportHTML }, data] = await Promise.all([
    import("../../../utils/ddReports/ddReport"),
    loadDdReportData(accessToken),
  ]);
  return buildDdReportHTML(lang, data);
}

const ddReportFileName = (lang) =>
  "Sustemia_OCDE_Informe_DD_" + lang.toUpperCase() + "_" + new Date().toISOString().slice(0, 10) + ".html";

const COMPARE_ROWS = [
  ["Para qué sirve", "Prepararte para una verificación FLA solicitada por una marca cliente.", "Construir y demostrar un sistema de HRDD alineado a la Guía OCDE y leyes de debida diligencia."],
  ["Qué evalúa", "Cumplimiento de la fábrica contra 9 elementos del Código y sus benchmarks.", "Madurez del sistema de gestión y riesgos en derechos humanos en 6 etapas."],
  ["Base normativa", "FLA Workplace Code, basado en convenios OIT.", "Guía OCDE de Debida Diligencia, Principios Rectores ONU, OIT."],
  ["Cómo se califica", "Compliant / Non-Compliant / Uncorroborated / N/A.", "Niveles de madurez 1–5 y scoring de significancia de riesgos."],
  ["Entregables", "Gap analysis, CAP, reporte de scoring imprimible.", "Risk Register, autoevaluación, repositorio de políticas, CAP, audit map."],
  ["Cuándo elegirla", "Tu cliente trabaja con FLA / Worldly y te pide un assessment.", "Necesitás cumplir CSDDD u otra ley HRDD, o construir el sistema desde cero."],
];

export function DDHome() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { methodology, chooseMethodology } = useDdMethodology(accessToken);
  const [compareOpen, setCompareOpen] = useState(false);
  const [example, setExample] = useState(null); // "fla" | "ocde" | null · uno abierto a la vez

  // Tocar una tarjeta ELIGE la metodología (se guarda por empresa) y lleva a su primer paso.
  const choose = (m) => {
    chooseMethodology(m);
    navigate(METHODOLOGIES[m].home);
  };

  // Abrir el ejemplo NO elige la metodología.
  const toggleExample = (e, m) => {
    e.stopPropagation();
    setExample((cur) => (cur === m ? null : m));
  };

  const cardCls = (m) => `ddh-card ddh-card--${m}${methodology === m ? " ddh-card--chosen" : ""}`;

  return (
    <div className="ddh-page">
      <DDNav />

      <div className="ddh-ph">
        <h1>Inicio</h1>
        <p>Elegí con qué metodología querés trabajar. Cada una tiene un propósito distinto.</p>
      </div>

      <div className="ddh-form">
        <h4>¿Qué necesitás hacer?</h4>

        <div className="ddh-cards">
          <div className={cardCls("fla")} onClick={() => choose("fla")}>
            <div className="ddh-card-top">
              <h2>Autoevaluar una fábrica</h2>
              <div className="ddh-sub">Código de Conducta FLA · 9 elementos</div>
              {methodology === "fla" && <span className="ddh-chosen">✓ Metodología elegida</span>}
            </div>
            <div className="ddh-card-body">
              <p className="ddh-desc"><b>Para una fábrica</b> que quiere medir su cumplimiento y prepararse para una verificación. Calificás 47 criterios y obtenés brechas, plan de acción y reporte.</p>
              <span className="ddh-go">{methodology === "fla" ? "Continuar autoevaluación →" : "Empezar autoevaluación →"}</span>
              <span className="ddh-ex-link" onClick={(e) => toggleExample(e, "fla")}>Ver ejemplo completo ↓</span>
            </div>
          </div>

          <div className={cardCls("ocde")} onClick={() => choose("ocde")}>
            <div className="ddh-card-top">
              <h2>Gestionar la debida diligencia</h2>
              <div className="ddh-sub">Guía OCDE 2021 · 6 etapas</div>
              {methodology === "ocde" && <span className="ddh-chosen">✓ Metodología elegida</span>}
            </div>
            <div className="ddh-card-body">
              <p className="ddh-desc"><b>Para tu fábrica</b> que necesita evaluar riesgos en su cadena y construir su sistema HRDD: Risk Register, autoevaluación, políticas y CAP.</p>
              <span className="ddh-go">{methodology === "ocde" ? "Continuar con OCDE →" : "Abrir metodología OCDE →"}</span>
              <span className="ddh-ex-link" onClick={(e) => toggleExample(e, "ocde")}>Ver ejemplo completo ↓</span>
            </div>
          </div>
        </div>

        {example === "fla"  && <ExampleFla  onClose={() => setExample(null)} />}
        {example === "ocde" && <ExampleOcde onClose={() => setExample(null)} />}

        <div className="ddh-compare">
          <button onClick={() => setCompareOpen(true)}>¿Cuál me conviene? Ver comparación →</button>
        </div>
      </div>

      {/* El panel OCDE se oculta si la empresa trabaja con FLA */}
      {methodology !== "fla" && (
        <>
          <div className="ddh-divider">
            <b>Panel · Debida Diligencia OCDE</b>
            <span />
            <small>resumen de la metodología OCDE</small>
          </div>
          <DDPanel />
        </>
      )}

      {methodology !== "fla" && (
            <DDReportCard
              variant="ocde"
              title="Informe completo de Debida Diligencia · descargable · ES / EN / PT"
              previewTitle="Vista previa del informe de Debida Diligencia"
              desc="Documento autocontenido que documenta y explica todo el proceso según la Guía OCDE 2021, sin ausencias: metodología con citas textuales, las 6 etapas con el checklist real «Proceso DD» (22 requisitos con evidencia), Risk Register con los 13 riesgos de los tres pilares —Social, Seguridad (SST) y Ambiente— con citas de la Guía, 99 factores contextuales y scoring por sede, autoevaluación de las 9 áreas con sus 39 criterios, las 20 políticas maestras con categoría, plantilla y estándares que las exigen, el mapa completo de las 9 auditorías del sector (96 secciones), plan de acción, conclusiones y anexos (repositorio y biblioteca). Se descarga en HTML y se imprime a PDF desde el propio informe."
              features={[
                ["1–3 · Resumen + etapas", "KPIs, sedes, «Cómo leer», glosario, metodología con citas y las 6 etapas con el checklist real del Proceso DD, flujo y cobertura del CAP."],
                ["4–5 · Riesgos + madurez", "Los 13 riesgos en tres pilares (social · SST · ambiente), 99 factores y scoring por sede; las 9 áreas con 39 criterios."],
                ["6–8 · Políticas + auditorías + CAP", "20 políticas con plantillas y estándares que las exigen, mapa de 9 auditorías (96 secciones) y plan de acción."],
                ["9–10 · Conclusiones + anexos", "Prioridades, próximos pasos, ficha con fuentes, repositorio y biblioteca de referencias."],
              ]}
              buildHtml={(lang) => buildDdReport(lang, accessToken)}
              fileName={ddReportFileName}
            />
      )}

      <div className="ddh-foot">
        Cada metodología guarda su avance de forma independiente, asociado a tu empresa; cambiar de metodología no borra nada.<br />
        <b>Nota:</b> herramientas de autodiagnóstico y preparación. No sustituyen una verificación independiente acreditada ni constituyen certificación.
      </div>

      {compareOpen && <CompareModal onClose={() => setCompareOpen(false)} />}
    </div>
  );
}

/* Ejemplos completos (spec v8, componente 4): texto fijo con los datos reales de la
 * demo. Si cambia la demo, actualizar estos textos para que sigan siendo reales. */

function ExampleFla({ onClose }) {
  return (
    <div className="ddh-ex ddh-ex--fla">
      <button className="ddh-ex-close" onClick={onClose}>✕ cerrar</button>
      <h5>Ejemplo completo · Autoevaluación FLA</h5>
      <div className="ddh-ex-sub">
        Así se ve el proceso con la fábrica de demostración incluida: <b>Confecciones del Bajío S.A.</b> · León, Guanajuato
        (México) · 820 trabajadores · calzado deportivo para PUMA y Nike · responsable: Laura Méndez, período FY2026.
      </div>
      <ol className="ddh-ex-steps">
        <li><b>Configurá la ficha.</b> Nombre, país, marcas, cantidad de trabajadores, responsable y período. Es lo que después encabeza el reporte.</li>
        <li>
          <b>Evaluá los 47 benchmarks.</b> Leés cada criterio del Código y marcás <b>Cumple</b>, <b>No cumple</b>, <b>Sin corroborar</b> (falta evidencia) o <b>No aplica</b>, anotando tu evidencia.
          <span className="ddh-ex-real">En el ejemplo quedaron 39 Cumple · 3 No cumple · 5 Sin corroborar · 47 de 47 respondidos.</span>
        </li>
        <li><b>El resultado se calcula solo.</b> Cumplidos ÷ aplicables: 39 ÷ 47 = <b>83% de cumplimiento</b> (semáforo amarillo, a 7 puntos del verde de 90%).</li>
        <li>
          <b>Las brechas se arman solas.</b> Todo «No cumple» o «Sin corroborar» pasa a la lista de brechas: <b>8 en el ejemplo</b>.
          <span className="ddh-ex-real">Una real: ER.18 · Conducta y disciplina progresiva — «el sistema disciplinario existe pero no se evidenció proceso de apelación ni testigo de tercera parte».</span>
        </li>
        <li>
          <b>Una acción por brecha.</b> Para cada brecha cargás causa raíz, acción, responsable, plazo y estado.
          <span className="ddh-ex-real">Una real: HSE.6 — causa raíz «falta de procedimiento de inspección periódica de rutas de evacuación» → acción «despejar y señalizar salidas; plan de evacuación publicado; inspección semanal documentada» · Coordinador de SST · vence 30-06-2026 · En progreso.</span>
        </li>
        <li><b>Reporte e informe.</b> La pestaña Reporte muestra el resultado imprimible y desde ahí descargás el informe completo del proceso en español, inglés o portugués.</li>
      </ol>
    </div>
  );
}

function ExampleOcde({ onClose }) {
  return (
    <div className="ddh-ex ddh-ex--ocde">
      <button className="ddh-ex-close" onClick={onClose}>✕ cerrar</button>
      <h5>Ejemplo completo · Debida Diligencia OCDE</h5>
      <div className="ddh-ex-sub">
        Así se ve el proceso con los datos de ejemplo de tu <b>Fábrica 1</b> (sede única; podés sumar sedes si hiciera falta).
      </div>
      <ol className="ddh-ex-steps">
        <li><b>Conocé el proceso.</b> La Guía ordena todo en 6 etapas; el módulo «Proceso DD» te da el checklist de 22 requisitos para marcarlas con evidencia, de «Sin evaluar» a «Implementado».</li>
        <li>
          <b>Puntuá los 13 riesgos (2.1).</b> Cada riesgo —social, de seguridad (SST) o ambiental— se puntúa de 1 a 5 en gravedad, alcance, irremediabilidad y probabilidad, marcando los factores presentes.
          <span className="ddh-ex-real">Ejemplo real: Acoso y VBG = gravedad 4 · alcance 4 · irremediabilidad 3 → severidad 3.7 × probabilidad 4 = <b>significancia 14.7 (Alto)</b>, con la nota «70% mujeres + supervisión masculina + factor sectorial maquila» y 3 factores marcados.</span>
        </li>
        <li><b>La plataforma prioriza sola.</b> Todo riesgo con significancia mayor a 5 queda como significativo: <b>12 de 13 en el ejemplo</b>. Esos van primero.</li>
        <li>
          <b>Autoevaluá las 9 áreas (2.2).</b> Ubicás cada área de gestión de 1 (Ausente) a 5 (Optimizado).
          <span className="ddh-ex-real">En el ejemplo, «Compra & precios» quedó en nivel 2 → brecha de madurez.</span>
        </li>
        <li>
          <b>Cada brecha lleva su acción (3.1).</b> El CAP junta riesgos y brechas: <b>12 acciones en el ejemplo</b> (11 en curso, 1 planificada), con responsable, plazo, indicador y avance.
          <span className="ddh-ex-real">Una real: CAP-2026-001 · Sustituir químicos peligrosos en denim por alternativas BAT · Fábrica 1 · 35% de avance · en curso.</span>
        </li>
        <li><b>Políticas y auditorías.</b> Las 20 políticas maestras arrancan en «No tiene»; a medida que subís cada una, el semáforo del tracker cambia y el Audit Map te muestra qué sección de SMETA, BSCI, SA8000 y los demás estándares respondés con ella.</li>
        <li><b>Informe final.</b> Desde este Inicio descargás el informe completo del proceso en 3 idiomas: etapas, riesgos con citas de la Guía, madurez, políticas, mapa de auditorías, CAP y conclusiones, todo referenciado.</li>
      </ol>
    </div>
  );
}

function CompareModal({ onClose }) {
  return (
    <div className="ddh-cmp-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ddh-cmp-modal">
        <div className="ddh-cmp-hd">
          <h3>FLA vs. OCDE · ¿cuál usar?</h3>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="ddh-cmp-body">
          <table className="ddh-cmp-table">
            <thead>
              <tr>
                <th>Criterio</th>
                <th className="ddh-cmp-th--fla">📝 FLA Workplace Code</th>
                <th className="ddh-cmp-th--ocde">🌐 Debida Diligencia OCDE</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map(([lbl, fla, ocde]) => (
                <tr key={lbl}>
                  <td className="ddh-cmp-lbl">{lbl}</td>
                  <td>{fla}</td>
                  <td>{ocde}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
